import m from './module';

/**
 * Fetches the merchandising brand/label scope for an event so the recap
 * response page can overlay product forms (form_type='product') with
 * merchandising rows. The endpoint returns:
 *   {
 *     enabled: boolean,
 *     allow_additional: boolean,
 *     effective_scope: [{ brands_id, brand_name, product_labels_id, product_label_name }, ...],
 *     additional_scope: [{ brands_id, brand_name, product_labels_id, product_label_name }, ...]
 *   }
 *
 * Each scope row is converted to a synthetic "product" object compatible with
 * the existing Form/FormResponseSet contracts: id is the product_labels_id,
 * filter arrays are empty so getFormResponseForProduct's role/location/locvar
 * filters short-circuit to "include all", and a _merchandising flag lets
 * downstream code distinguish merchandising rows from legacy products-table
 * rows.
 */
class MerchandisingRecapScopeService {
  constructor($q, APIService, LogService) {
    "ngInject";
    this.$q = $q;
    this.APIService = APIService;
    this.LogService = LogService;
  }

  /**
   * @param {number} eventsId
   * @returns {Promise<{enabled: boolean, allow_additional: boolean, effective: Array, additional: Array}>}
   */
  getForEvent(eventsId) {
    if (!eventsId) {
      return this.$q.when(this._empty());
    }
    const path = `/event/${eventsId}/merchandising/recap_scope`;
    return this.APIService.get(path, { force: true })
      .then(response => this._normalize(response.data || response))
      .catch(err => {
        this.LogService.error('Error fetching merchandising recap scope:', err);
        // Failing soft here so the recap page still loads with the legacy
        // product flow; an HTTP error shouldn't block all merchandising recaps.
        return this._empty();
      });
  }

  /**
   * Offline-friendly counterpart to getForEvent: read the scope already
   * embedded on a synced event (event.merchandising_recap_scope, populated by
   * the staff EventSerializer) instead of hitting the live endpoint. Returns
   * the same normalized shape. Empty when the event carries no scope.
   *
   * @param {object} event
   * @returns {{enabled: boolean, allow_additional: boolean, effective: Array, additional: Array}}
   */
  getFromEvent(event) {
    const raw = event && event.merchandising_recap_scope;
    if (!raw) {
      return this._empty();
    }
    return this._normalize(raw);
  }

  /**
   * Normalize the raw recap_scope payload (live endpoint or synced event) into
   * the shape the overlay + Form/FormResponseSet code paths expect.
   *
   * @param {object} data
   * @returns {object}
   */
  _normalize(data) {
    data = data || {};
    return {
      enabled: !!data.enabled,
      allow_additional: !!data.allow_additional,
      effective: (data.effective_scope || []).map(this._toProduct),
      additional: (data.additional_scope || []).map(this._toProduct),
      merchandising_enabled: !!data.merchandising_enabled,
      event_merchandising_activities_ids: (data.event_merchandising_activities_ids || [])
        .map(id => Number(id))
        .filter(id => Number.isFinite(id) && id > 0),
      // {id,name} of the event's program's active activity types - the options
      // for a merchandising form's per-response activity-type dropdown.
      event_merchandising_activities: (data.event_merchandising_activities || [])
        .map(a => ({
          id: Number(a && a.id),
          name: String((a && a.name) || ''),
          // Whether this activity type uses the customer/product pickers.
          // Default true; only an explicit false suppresses them.
          show_product_selection: !(a && a.show_product_selection === false),
        }))
        .filter(a => Number.isFinite(a.id) && a.id > 0),
      // Survey-form per-instance product pool. Independent of `enabled` (which
      // is the legacy/scope-mode toggle) so it works on clients running
      // use_legacy_products=true. survey_product_labels is the full program
      // pool; survey_event_product_labels is just the event-selected products.
      // survey_allow_additional decides which the per-instance product picker
      // uses (program pool when on, event-only when off).
      survey_product_labels: (data.survey_product_labels || []).map(this._toProduct),
      survey_event_product_labels: (data.survey_event_product_labels || []).map(this._toProduct),
      survey_allow_additional: !!data.survey_allow_additional,
      sampling_enabled: !!data.sampling_enabled,
      sampling_feedback_enabled: !!data.sampling_feedback_enabled,
    };
  }

  /**
   * Apply the merchandising scope to a loaded recap response's definition:
   * overlays product forms, seeds per-instance survey pickers (activity-type +
   * product) and the event-selected default products, and filters out forms
   * whose activity types don't intersect the event's. Shared by the admin
   * (online) and staff (offline) recap controllers so both behave identically.
   *
   * @param {object} recapResponse
   * @param {object} scope normalized scope (getForEvent / getFromEvent)
   */
  applyOverlay(recapResponse, scope) {
    if (!recapResponse) return;
    this._applyMerchandisingScopeOverlay(recapResponse, scope);
    this._applySurveyFormProductOverlay(recapResponse, scope);
    this._applyActivityTypesFormFilter(recapResponse, scope);
    this._seedMerchandisingInstances(recapResponse);
  }

  /**
   * Seed default instances on every merchandising form's response set: one per
   * event-selected product. Push-based (called once here, after the survey
   * overlay set each form's _merchandisingDefaultProducts) rather than lazily
   * inside getOrCreateFormResponses - the lazy approach created a stray empty
   * instance before the scope was applied and could seed stale defaults from
   * the cached recap definition.
   *
   * @param {object} recapResponse
   */
  _seedMerchandisingInstances(recapResponse) {
    const pageResponses = (recapResponse && recapResponse.allPageResponses) || [];
    for (const pageResponse of pageResponses) {
      const sets = (pageResponse && pageResponse.form_response_sets) || [];
      for (const set of sets) {
        if (set && set.form && set.form.isProductScopeForm
            && typeof set.ensureMerchandisingDefaultInstances === 'function') {
          set.ensureMerchandisingDefaultInstances();
        }
      }
    }
  }

  /**
   * Replace each product form's products array with the merchandising effective
   * rows. No-op when the scope is disabled (legacy products-table flow).
   */
  _applyMerchandisingScopeOverlay(recapResponse, scope) {
    if (!scope || !scope.enabled) return;
    const productForms = this._collectForms(recapResponse, 'isProductForm');
    if (!productForms.length) return;
    productForms.forEach(form => {
      // Idempotent: if the user already pushed "additional" merchandising rows,
      // leave form.products alone; only refresh the additional pool.
      if (!form._merchandising) {
        form.products = scope.effective.slice();
        form._merchandising = true;
      }
      form._merchandisingAdditionalScope = scope.additional;
      form._merchandisingAllowAdditional = scope.allow_additional;
    });
  }

  /**
   * Per-instance product picker overlay for product-scope (survey-style) forms:
   * merchandising and sampling. Seeds the picker pool plus the event-selected
   * default products (which seed one default instance each). Merchandising forms
   * also receive the event's activity-type restriction; sampling forms skip
   * activities entirely and instead pick up the program-level feedback flag.
   *
   * Merchandising forms are gated on merchandising_enabled and sampling forms on
   * sampling_enabled, so a client running only one module never seeds the other.
   */
  _applySurveyFormProductOverlay(recapResponse, scope) {
    if (!scope || (!scope.merchandising_enabled && !scope.sampling_enabled)) return;
    const forms = this._collectForms(recapResponse, 'isProductScopeForm');
    if (!forms.length) return;

    const programPool = Array.isArray(scope.survey_product_labels) ? scope.survey_product_labels : [];
    const eventPool = Array.isArray(scope.survey_event_product_labels) ? scope.survey_event_product_labels : [];
    // Ensure pre-selected products are also part of the pool
    let pool;
    if (scope.survey_allow_additional) {
      const seen = new Set(programPool.map(p => Number(p && p.id)));
      pool = programPool.concat(eventPool.filter(p => !seen.has(Number(p && p.id))));
    } else {
      pool = eventPool;
    }
    const eventActivityIds = (scope.event_merchandising_activities_ids || []).slice();
    // Full {id,name} list of the event's program's active activity types -
    // these are the activity-type dropdown options on a merchandising form.
    const eventActivities = Array.isArray(scope.event_merchandising_activities)
      ? scope.event_merchandising_activities.slice()
      : [];

    forms.forEach(form => {
      const moduleEnabled = form.isSamplingForm ? scope.sampling_enabled : scope.merchandising_enabled;
      if (!moduleEnabled) return;

      // Always (re)apply: the recap definition (and its form objects) is cached
      // and reused across recap loads, so these must refresh every overlay or
      // a prior event's pool/defaults would stick.
      //  - _merchandisingEventActivityIds: the event's activity-type filter.
      //  - _merchandisingDefaultProducts: the event-selected products that seed
      //    one default instance each (regardless of the picker pool below).
      //  - products: the per-instance product picker pool - the full program
      //    pool when allow-additional is on, the event-selected set when off.
      // Activity fields are meaningless for sampling forms (no activity picker
      // is shown) but harmless to set; kept unconditional to avoid branching.
      form._merchandisingEventActivityIds = eventActivityIds;
      form._merchandisingEventActivities = eventActivities.slice();
      form._merchandisingDefaultProducts = eventPool.slice();
      form.products = pool.slice();
      form._merchandisingSurvey = true;
      form._merchandisingAllowAdditional = !!scope.survey_allow_additional;
      form._samplingFeedbackEnabled = !!(form.isSamplingForm && scope.sampling_feedback_enabled);
    });
  }

  /**
   * Drop forms whose merchandising activity types don't intersect the event's.
   * Forms with no activity types attached are always kept. No-op when the
   * Merchandising module is disabled.
   */
  _applyActivityTypesFormFilter(recapResponse, scope) {
    if (!scope || !scope.merchandising_enabled) return;
    const definition = recapResponse && recapResponse.recap_definition;
    const pages = (definition && definition.pages) || [];
    const eventActivityIds = new Set(scope.event_merchandising_activities_ids || []);

    for (const page of pages) {
      const items = (page && page.items) || [];
      const originalItems = items.slice();
      for (const form of originalItems) {
        if (!form || typeof form !== 'object' || !form.isForm) continue;
        const formActivityIds = (form.merchandising_activities || [])
          .map(a => Number(a && a.id))
          .filter(id => Number.isFinite(id) && id > 0);
        if (formActivityIds.length === 0) continue;
        const matches = formActivityIds.some(id => eventActivityIds.has(id));
        if (!matches) {
          const idx = items.indexOf(form);
          if (idx !== -1) items.splice(idx, 1);
        }
      }
    }
  }

  /**
   * Collect every form in a recap definition matching a Form getter
   * (e.g. 'isProductForm', 'isMerchandisingForm').
   *
   * @param {object} recapResponse
   * @param {string} predicate Form boolean getter name
   * @returns {Array}
   */
  _collectForms(recapResponse, predicate) {
    const out = [];
    const definition = recapResponse && recapResponse.recap_definition;
    const pages = (definition && definition.pages) || [];
    for (const page of pages) {
      const items = (page && page.items) || [];
      for (const form of items) {
        if (form && typeof form === 'object' && form[predicate]) {
          out.push(form);
        }
      }
    }
    return out;
  }

  _empty() {
    return {
      enabled: false,
      allow_additional: false,
      effective: [],
      additional: [],
      merchandising_enabled: false,
      event_merchandising_activities_ids: [],
      survey_product_labels: [],
      survey_event_product_labels: [],
      survey_allow_additional: false,
      sampling_enabled: false,
      sampling_feedback_enabled: false,
    };
  }

  /**
   * Map a raw scope row to the shape the Form/FormResponseSet/FormResponse
   * code paths expect on form.products[]. The id is the product_labels_id;
   * filter arrays are empty so all responses pass the visibility filter; the
   * _merchandising flag drives the alternate id field on serialize.
   *
   * @param {object} row
   * @returns {object}
   */
  _toProduct(row) {
    const brand = row.brand_name || '';
    const label = row.product_label_name || '';
    return {
      id: row.product_labels_id,
      product_labels_id: row.product_labels_id,
      brands_id: row.brands_id,
      brand_name: brand,
      product_label_name: label,
      // Supplier (customer) decoration for the per-response supplier filter.
      supplier_id: row.supplier_id != null ? Number(row.supplier_id) || null : null,
      supplier_label: String(row.supplier_label || ''),
      name: brand && label ? `${brand} ${label}` : (brand || label),
      _merchandising: true,
      location_variables: [],
      role_variables: [],
      locations: [],
    };
  }
}

export default MerchandisingRecapScopeService;

m.service('MerchandisingRecapScopeService', MerchandisingRecapScopeService);
